// Structured logging utility for real-time production monitoring
// Works great with: vercel logs --follow
// Outputs JSON logs that are easy to parse and filter
//
// To disable logging, set ENABLE_STRUCTURED_LOGGING=false in your environment

export type LogLevel = 'error' | 'warn' | 'info' | 'debug'

// Check if structured logging is enabled (default: true)
const isLoggingEnabled = () => {
  const envFlag = process.env.ENABLE_STRUCTURED_LOGGING
  // If explicitly set to 'false', disable logging
  if (envFlag === 'false' || envFlag === '0') {
    return false
  }
  // Default to enabled
  return true
}

export interface LogContext {
  route?: string
  endpoint?: string
  method?: string
  userId?: string
  userEmail?: string
  userRole?: string
  requestId?: string
  duration?: number
  statusCode?: number
  errorType?: string
  stack?: string
  [key: string]: any
}

interface LogEntry {
  type: 'log'
  level: LogLevel
  message: string
  timestamp: string
  context?: LogContext
}

/**
 * Structured logger that outputs JSON for easy parsing with vercel logs --follow
 */
class Logger {
  private formatLog(level: LogLevel, message: string, context?: LogContext): string {
    const logEntry: LogEntry = {
      type: 'log',
      level,
      message,
      timestamp: new Date().toISOString(),
      context: context ? {
        ...context,
        // Ensure stack is included if present
        ...(context.stack ? { stack: context.stack } : {})
      } : undefined
    }

    return JSON.stringify(logEntry)
  }

  error(message: string, context?: LogContext): void {
    if (!isLoggingEnabled()) return
    const log = this.formatLog('error', message, context)
    console.error(log)
  }

  warn(message: string, context?: LogContext): void {
    if (!isLoggingEnabled()) return
    const log = this.formatLog('warn', message, context)
    console.warn(log)
  }

  info(message: string, context?: LogContext): void {
    if (!isLoggingEnabled()) return
    const log = this.formatLog('info', message, context)
    console.log(log)
  }

  debug(message: string, context?: LogContext): void {
    if (!isLoggingEnabled()) return
    if (process.env.NODE_ENV === 'development') {
      const log = this.formatLog('debug', message, context)
      console.log(log)
    }
  }

  /**
   * Log API route errors with full context
   */
  apiError(
    error: Error | string,
    request: Request,
    context?: LogContext
  ): void {
    const errorMessage = error instanceof Error ? error.message : error
    const stack = error instanceof Error ? error.stack : undefined
    
    const url = new URL(request.url)
    
    this.error(errorMessage, {
      route: url.pathname,
      endpoint: url.pathname,
      method: request.method,
      errorType: error instanceof Error ? error.name : 'UnknownError',
      stack,
      ...context
    })
  }

  /**
   * Log API route success with timing
   */
  apiSuccess(
    route: string,
    method: string,
    duration: number,
    statusCode: number = 200,
    context?: LogContext
  ): void {
    this.info(`API ${method} ${route} completed`, {
      route,
      endpoint: route,
      method,
      duration,
      statusCode,
      ...context
    })
  }

  /**
   * Log external API calls
   */
  externalApi(
    service: string,
    url: string,
    method: string,
    duration: number,
    statusCode?: number,
    error?: Error | string,
    context?: LogContext
  ): void {
    if (error) {
      const errorMessage = error instanceof Error ? error.message : error
      const stack = error instanceof Error ? error.stack : undefined
      
      this.error(`External API error: ${service}`, {
        external: {
          service,
          url,
          method,
          statusCode
        },
        errorType: error instanceof Error ? error.name : 'UnknownError',
        stack,
        duration,
        ...context
      })
    } else {
      this.info(`External API call: ${service}`, {
        external: {
          service,
          url,
          method,
          statusCode
        },
        duration,
        ...context
      })
    }
  }

  /**
   * Log database operations
   */
  database(
    operation: string,
    table?: string,
    duration?: number,
    error?: Error | string,
    context?: LogContext
  ): void {
    if (error) {
      const errorMessage = error instanceof Error ? error.message : error
      const stack = error instanceof Error ? error.stack : undefined
      
      this.error(`Database error: ${operation}`, {
        database: {
          operation,
          table
        },
        errorType: error instanceof Error ? error.name : 'UnknownError',
        stack,
        duration,
        ...context
      })
    } else {
      this.info(`Database operation: ${operation}`, {
        database: {
          operation,
          table
        },
        duration,
        ...context
      })
    }
  }

  /**
   * Log authentication events
   */
  auth(
    event: 'signup' | 'signin' | 'signout' | 'token_refresh' | 'token_expired' | 'unauthorized',
    userId?: string,
    userEmail?: string,
    error?: Error | string,
    context?: LogContext
  ): void {
    if (error) {
      const errorMessage = error instanceof Error ? error.message : error
      const stack = error instanceof Error ? error.stack : undefined
      
      this.error(`Auth error: ${event}`, {
        auth: {
          event
        },
        userId,
        userEmail,
        errorType: error instanceof Error ? error.name : 'UnknownError',
        stack,
        ...context
      })
    } else {
      this.info(`Auth event: ${event}`, {
        auth: {
          event
        },
        userId,
        userEmail,
        ...context
      })
    }
  }

  /**
   * Log performance issues
   */
  performance(
    issue: string,
    duration: number,
    threshold: number,
    context?: LogContext
  ): void {
    if (duration > threshold) {
      this.warn(`Performance issue: ${issue}`, {
        performance: {
          issue,
          duration,
          threshold
        },
        ...context
      })
    }
  }
}

// Export singleton instance
export const logger = new Logger()

// Helper function to extract user context from request (if available)
export function extractUserContext(request?: Request): Partial<LogContext> {
  // This would need to be adapted based on your auth system
  // For now, return empty object
  return {}
}

// Helper to create request context
export function createRequestContext(
  request: Request,
  additionalContext?: LogContext
): LogContext {
  const url = new URL(request.url)
  
  return {
    route: url.pathname,
    endpoint: url.pathname,
    method: request.method,
    ...additionalContext
  }
}

